import { useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import Icon from '../../atoms/Icon';
import { Headline500 } from '../../atoms/Headline';
import { HeadlineLevel } from '../../atoms/Headline/Headline';
import { CopyStandardSkin } from '../../atoms/Copytext/CopytextSkins';
import { mq } from '~/styles/grid';
import getFontFamily from '~/helpers/getFontFamily';
import { getColorByName } from '~/helpers/getColors';

/**
 * @param {TeaserColorType} bgColor
 * @param {Theme} theme
 * @returns {string}
 */
const getBgColor = (bgColor, theme) => {
  switch (bgColor) {
    case 'BRAND':
      return theme.color.gradientprimary500;
    case 'GRAY':
      return theme.color.gray400;
    case 'NONE':
      return theme.color.white500;
    default:
      return theme.color.white500;
  }
};

/**
 * @param {TeaserColorType} bgColor
 * @param {Theme} theme
 * @returns {string}
 */
const getFontColor = (bgColor, theme) => {
  if (bgColor === 'BRAND') {
    return theme.color.white500;
  }
  return theme.color.primary800;
};

const CareerTeaserContainerCss = css`
  text-decoration: none;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 60px 20px 30px;
  background: ${(props) => getBgColor(props.bgColor, props.theme)};

  @media (${(props) => props.theme.teaserCareer.mobileSwitch}) {
    padding: 40px 20px 30px;
    width: 100%;
  }
`;

const CareerTeaserContainerLink = styled.a`
  ${CareerTeaserContainerCss};
`;

const CareerTeaserContainerDiv = styled.div`
  ${CareerTeaserContainerCss};
`;

const StyledIcon = styled.img`
  width: 70px;
  height: 70px;
`;

const IconPlaceholder = styled.div`
  width: 70px;
  height: 70px;
`;

const StyledHeadline = styled(Headline500)`
  margin: 30px 0 0;
  color: ${(props) => getFontColor(props.bgColor, props.theme)} !important;

  @media (${mq.mdDown}) {
    font-family: ${(props) => getFontFamily(props.theme.headline.h500.xs.font)};
    font-size: ${(props) => props.theme.headline.h500.xs.fontSize};
    line-height: ${(props) => props.theme.headline.h500.xs.lineHeight};
  }
`;

const StyledCopy = styled.span`
  ${CopyStandardSkin};
  color: ${(props) =>
    props.bgColor === 'BRAND'
      ? `${props.theme.color.white500} !important`
      : `${getColorByName(
          props.theme,
          props.theme.copytext.copyStandard.textColor,
        )} !important`};
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: auto;
  display: block;
  margin: 15px 0 30px;
`;

const ArrowContainer = styled.div`
  align-self: flex-end;
`;

/**
 * @param {TeaserCareerProps} props
 * @returns {React.ReactElement} The rendered component
 */
const TeaserCareer = ({
  bgColor = 'NONE',
  iconUrl,
  headline,
  text,
  link,
  className,
}) => {
  const [hovered, isHover] = useState(false);
  const theme = useTheme();

  const TeaseContent = () => {
    return (
      <>
        {iconUrl && <StyledIcon src={iconUrl} alt={headline} />}
        {!iconUrl && <IconPlaceholder />}
        <StyledHeadline bgColor={bgColor} level={HeadlineLevel.h3}>
          {headline}
        </StyledHeadline>
        <StyledCopy bgColor={bgColor}>{text}</StyledCopy>
        {link?.url && (
          <ArrowContainer data-id="teaser-career-arrow-container">
            <Icon
              icon="ARROW_ANIMATION"
              checked={hovered}
              color={getFontColor(bgColor, theme)}
              height={20}
              width={20}
              tag="none"
            />
          </ArrowContainer>
        )}
      </>
    );
  };

  if (link?.url && link?.url !== '') {
    return (
      <CareerTeaserContainerLink
        href={link?.url}
        className={className}
        target={link?.target ?? '_SELF'}
        bgColor={bgColor}
        onMouseEnter={() => isHover(true)}
        onMouseLeave={() => isHover(false)}
      >
        {TeaseContent()}
      </CareerTeaserContainerLink>
    );
  }

  return (
    <CareerTeaserContainerDiv className={className} bgColor={bgColor}>
      {TeaseContent()}
    </CareerTeaserContainerDiv>
  );
};
export default TeaserCareer;
